import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, Filter, RefreshCcw } from "lucide-react";

const CATS = ["", "auth", "consent", "submission", "campaign", "content", "policy", "section", "backup"];

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("");
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    q.set("limit", "500");
    api.get(`/admin/audit-log?${q}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [category]);

  const filtered = useMemo(() => {
    return items.filter((r) => {
      if (actor && !(r.actor || "").toLowerCase().includes(actor.toLowerCase())) return false;
      if (action && !(r.action || "").toLowerCase().includes(action.toLowerCase())) return false;
      if (fromDate && (r.timestamp || "") < fromDate) return false;
      if (toDate && (r.timestamp || "") > toDate + "T23:59:59") return false;
      return true;
    });
  }, [items, actor, action, fromDate, toDate]);

  const download = (fmt) => window.open(`${API}/admin/audit-log/export?format=${fmt}`, "_blank");
  const clearFilters = () => { setActor(""); setAction(""); setFromDate(""); setToDate(""); setCategory(""); };

  return (
    <div data-testid="admin-audit">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="eyebrow">Immutable log</span>
          <h1 className="mt-2 text-3xl text-navy">Activity Log · Governance Audit</h1>
          <p className="mt-2 text-sm text-navy/60 max-w-2xl">
            Every action on the platform is written here. Filter by user, action, category or date, and export a formal report.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-outline-navy !py-2 !px-4 text-sm" onClick={load} data-testid="audit-refresh">
            <RefreshCcw size={14} /> Refresh
          </button>
          <button className="btn-outline-navy !py-2 !px-4 text-sm" onClick={() => download("csv")} data-testid="audit-export-csv">
            <Download size={14} /> CSV
          </button>
          <button className="btn-outline-navy !py-2 !px-4 text-sm" onClick={() => download("json")} data-testid="audit-export-json">
            <Download size={14} /> JSON
          </button>
          <button className="btn-gold !py-2 !px-4 text-sm" onClick={() => download("pdf")} data-testid="audit-export-pdf">
            <FileText size={14} /> PDF Report
          </button>
        </div>
      </div>
      <span className="divider-gold" />

      {/* Filters card */}
      <div className="card-elev p-5 mb-5" data-testid="audit-filters">
        <div className="flex items-center gap-2 text-navy/70 text-xs uppercase tracking-widest mb-4">
          <Filter size={14} className="text-gold" /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="text-xs text-navy/60 block mb-1">Category</label>
            <select
              data-testid="audit-filter-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="field-box !py-2"
            >
              {CATS.map((c) => (<option key={c || "all"} value={c}>{c || "All categories"}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs text-navy/60 block mb-1">User (email)</label>
            <input
              data-testid="audit-filter-actor"
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              placeholder="e.g. admin@…"
              className="field-box !py-2"
            />
          </div>
          <div>
            <label className="text-xs text-navy/60 block mb-1">Action contains</label>
            <input
              data-testid="audit-filter-action"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="e.g. login, quote"
              className="field-box !py-2"
            />
          </div>
          <div>
            <label className="text-xs text-navy/60 block mb-1">From date</label>
            <input
              data-testid="audit-filter-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="field-box !py-2"
            />
          </div>
          <div>
            <label className="text-xs text-navy/60 block mb-1">To date</label>
            <input
              data-testid="audit-filter-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="field-box !py-2"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 text-xs text-navy/60 uppercase tracking-widest">
          <span data-testid="audit-count">{filtered.length} / {items.length} records</span>
          <button onClick={clearFilters} className="ms-auto text-navy/70 underline decoration-gold/60 underline-offset-4">
            Clear filters
          </button>
        </div>
      </div>

      <div className="card-elev overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy text-cream">
            <tr>
              <th className="text-start p-3">Time</th>
              <th className="text-start p-3">Category</th>
              <th className="text-start p-3">Action</th>
              <th className="text-start p-3">User</th>
              <th className="text-start p-3">Target</th>
              <th className="text-start p-3">IP</th>
              <th className="text-start p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-navy/50">Loading…</td></tr>}
            {!loading && filtered.map((e) => (
              <tr key={e.id} data-testid={`audit-row-${e.id}`} className="border-t border-navy/10 hover:bg-cream transition-colors">
                <td className="p-3 text-navy/80 font-mono text-xs whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                <td className="p-3"><Badge variant="secondary">{e.category}</Badge></td>
                <td className="p-3 text-navy">{e.action}</td>
                <td className="p-3 text-navy/80">{e.actor}</td>
                <td className="p-3 text-navy/70 text-xs">
                  {e.target_type ? `${e.target_type}:${(e.target_id || "").slice(0, 8)}` : "—"}
                </td>
                <td className="p-3 text-navy/60 font-mono text-xs">{e.ip || "—"}</td>
                <td className="p-3">
                  <Badge className={e.status === "success" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                    {e.status}
                  </Badge>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-navy/50">No entries match filters</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
